/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

// import "./KatexEquationAlterer.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import * as React from "react";
import { useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import KatexRenderer from "./KatexRenderer";
import TextareaInput from "../../../../TextareaInput";
import Text from "../../../../Text";
import { css } from "@linaria/core";
import { colors, spacings } from "../../../../Theme";
import t from "../../../../i18n";
import TooltipV2 from "../../../../TooltipV2";
import OverlayWrapper from "../../../../TooltipV2/OverlayWrapper";
import { InformationLine } from "../../../../IconV2/react";
import { color, fonts } from "../../../..";
import { translationLocale } from "../../../../i18n";
import { getHelpCenterUrl } from "../../../../lib/helpCenter";

type Props = {
  initialEquation?: string;
  onEquation: (equation: string, inline: boolean) => void;
};

export default function KatexEquationAlterer({
  onEquation,
  initialEquation = "",
}: Props): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [equation, setEquation] = useState<string>(initialEquation);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEquation(e.target.value);
      onEquation(e.target.value, false);
    },
    [onEquation],
  );

  const linkPath = React.useMemo(
    () =>
      getHelpCenterUrl(
        translationLocale === "fr" ? "dashboards#bloc-texte" : "dashboards#text-block",
        translationLocale,
      ),
    [],
  );

  return (
    <>
      <div>
        <div className={katexEquationAltererLabel}>
          <Text type="body1SemiBold" className={label}>
            {t("markdown_editor.plugin.equation.input_label")}
          </Text>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                className={overlayTooltip}
                text={
                  <div>
                    <Text type="body2">
                      {t("markdown_editor.plugin.equation.tooltip.text.description")}
                    </Text>
                    <a
                      className={overlayTooltipLink}
                      href={linkPath}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("markdown_editor.plugin.equation.tooltip.text.link")}
                    </a>
                  </div>
                }
              />
            }
          >
            <div>
              <InformationLine color={color.carbon800} />
            </div>
          </TooltipV2>
        </div>
        <TextareaInput
          onChange={onChange}
          value={equation}
          className="KatexEquationAlterer_textArea"
        />
      </div>
      <Text type="body1SemiBold" className={label}>
        {t("markdown_editor.plugin.equation.preview_label")}
      </Text>
      {equation.trim() === "" ? (
        <Text type="body2" className={katexPreviewEmpty}>
          {t("markdown_editor.plugin.equation.preview_label.empty")}
        </Text>
      ) : (
        <div className={katexPreview}>
          <ErrorBoundary onError={e => editor._onError(e)} fallback={null}>
            <KatexRenderer equation={equation} inline={false} onDoubleClick={() => null} />
          </ErrorBoundary>
        </div>
      )}
    </>
  );
}

//#region CSS
const katexPreviewEmpty = css`
  display: grid;
  place-items: center;
  color: ${colors.carbon700};
  height: 66px;
  width: 100%;
`;
const katexEquationAltererLabel = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;
const overlayTooltip = css`
  max-width: 276px;
`;
const overlayTooltipLink = css`
  display: block;
  margin-top: ${spacings.xs};
  font: ${fonts.body2};
  color: ${color.lightNitrogen500_Main};
  :hover {
    text-decoration: underline;
    color: ${color.lightNitrogen600};
  }
`;
const label = css`
  color: ${colors.carbon800};
  margin-bottom: 4px;
`;
const katexPreview = css`
  width: 100%;
  overflow-y: auto;
`;
//#endregion CSS
